import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ProblemError, setCaseStatus, type CaseStatus } from "@/api";

const STAGE_MOVED = "Stadium hat sich inzwischen geändert.";
/**
 * The status route's own sentence when the Vorgang was handed over a moment
 * ago (Plan C, `workflow/service.py` `_NOT_YOURS_ANYMORE`). A different fact
 * from „the stage moved", so it is said in the server's words — the record
 * reloads into „nicht gefunden" right after (both caches are refreshed).
 */
const NOT_YOURS_ANYMORE = "Dieser Vorgang gehört inzwischen jemand anderem.";

/**
 * The longest `busy` waits for the refreshed answer after the move itself
 * has come back. The app retries a failed query once, so an outage settles
 * within about a second; a refetch that hangs must not leave every gesture
 * disabled.
 */
export const REFRESH_WAIT_MS = 3000;

/**
 * One manual stage move (`POST /cases/{id}/status`), with the refusal as a
 * sentence and both caches refreshed either way: the register and the start
 * list live under `["cases"]`, the record under `["case", id]`.
 *
 * `busy` stays up until the refreshed answer is in, not only the move
 * (register fix round 4, M1): a control that looks after the focus once the
 * gesture is back then sees the new status in the same moment — accepted or
 * refused — instead of a status that may or may not still change.
 */
export function useStageChange() {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function stage(id: string, to: CaseStatus) {
    setBusy(true);
    setNotice(null);
    try {
      await setCaseStatus(id, to);
    } catch (cause) {
      setNotice(
        cause instanceof ProblemError && cause.status === 409
          ? cause.userMessage === NOT_YOURS_ANYMORE
            ? NOT_YOURS_ANYMORE
            : STAGE_MOVED
          : cause instanceof ProblemError
            ? cause.userMessage
            : "Stadium nicht gesetzt — Server nicht erreichbar.",
      );
    } finally {
      const refreshed = Promise.all([
        client.invalidateQueries({ queryKey: ["cases"] }),
        client.invalidateQueries({ queryKey: ["case", id] }),
      ]).catch(() => undefined);
      let timer: ReturnType<typeof setTimeout> | undefined;
      const cap = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, REFRESH_WAIT_MS);
      });
      await Promise.race([refreshed, cap]);
      clearTimeout(timer);
      setBusy(false);
    }
  }

  /**
   * Drops a standing refusal notice (register fix round 1, I4): a rejected
   * gesture's sentence used to sit in the status line forever, because
   * nothing but another gesture ever replaced it. A search, a filter change
   * or a reset is a fresh situation and clears it.
   */
  function clear() {
    setNotice(null);
  }

  return { busy, notice, stage, clear };
}
